import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import linguistApi from "services/linguistApi";

const BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY = "ce_portal.browserNotificationsEnabled";
const SMS_NOTIFICATION_ENABLED_STORAGE_KEY = "ce_portal.smsNotificationsEnabled";
const QUIET_HOURS_START_STORAGE_KEY = "ce_portal.quietHoursStart";
const QUIET_HOURS_END_STORAGE_KEY = "ce_portal.quietHoursEnd";
const TIMEZONE_STORAGE_KEY = "ce_portal.notificationTimezone";

const DEFAULT_QUIET_START = "22:00";
const DEFAULT_QUIET_END = "07:00";

const formatTime12Hour = (timeValue) => {
  const normalized = String(timeValue || "").trim();
  const [rawHour, rawMinute] = normalized.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return normalized || "N/A";
  }

  const safeHour = ((hour % 24) + 24) % 24;
  const period = safeHour >= 12 ? "PM" : "AM";
  const hour12 = safeHour % 12 === 0 ? 12 : safeHour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
};

const buildTimeOptions = (stepMinutes = 15) => {
  const options = [];

  for (let totalMinutes = 0; totalMinutes < 24 * 60; totalMinutes += stepMinutes) {
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    options.push({ value, label: formatTime12Hour(value) });
  }

  return options;
};

const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
];

const getDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (error) {
    return "America/New_York";
  }
};

const LinguistNotificationSettingsEditOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, triggerPageRefresh } = useStateContext();

  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(
    overlayData?.settings?.BrowserNotificationsEnabled !== false,
  );
  const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(
    overlayData?.settings?.SmsNotificationsEnabled === true,
  );
  const [quietHoursStart, setQuietHoursStart] = useState(
    overlayData?.settings?.QuietHoursStart || DEFAULT_QUIET_START,
  );
  const [quietHoursEnd, setQuietHoursEnd] = useState(
    overlayData?.settings?.QuietHoursEnd || DEFAULT_QUIET_END,
  );
  const [timezone, setTimezone] = useState(
    overlayData?.settings?.Timezone || getDefaultTimezone(),
  );

  const timeOptions = useMemo(() => {
    const baseOptions = buildTimeOptions(15);
    const optionMap = new Map(baseOptions.map((option) => [option.value, option]));

    [quietHoursStart, quietHoursEnd].forEach((timeValue) => {
      const normalized = String(timeValue || "").trim();
      if (!normalized || optionMap.has(normalized)) {
        return;
      }

      optionMap.set(normalized, {
        value: normalized,
        label: formatTime12Hour(normalized),
      });
    });

    return [...optionMap.values()];
  }, [quietHoursStart, quietHoursEnd]);

  const canSave = useMemo(() => Boolean(quietHoursStart && quietHoursEnd && timezone), [quietHoursStart, quietHoursEnd, timezone]);

  const closeOverlay = () => setOverlayName("");

  const handleSave = async () => {
    try {
      const payload = {
        NotificationSettings: {
          BrowserNotificationsEnabled: browserNotificationsEnabled === true,
          SmsNotificationsEnabled: smsNotificationsEnabled === true,
          QuietHoursStart: quietHoursStart || DEFAULT_QUIET_START,
          QuietHoursEnd: quietHoursEnd || DEFAULT_QUIET_END,
          Timezone: timezone || getDefaultTimezone(),
        },
      };

      await linguistApi.put("/me", payload);

      window.localStorage.setItem(
        BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY,
        String(browserNotificationsEnabled === true),
      );
      window.localStorage.setItem(
        SMS_NOTIFICATION_ENABLED_STORAGE_KEY,
        String(smsNotificationsEnabled === true),
      );
      window.localStorage.setItem(
        QUIET_HOURS_START_STORAGE_KEY,
        quietHoursStart || DEFAULT_QUIET_START,
      );
      window.localStorage.setItem(
        QUIET_HOURS_END_STORAGE_KEY,
        quietHoursEnd || DEFAULT_QUIET_END,
      );
      window.localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone || getDefaultTimezone());

      window.dispatchEvent(
        new CustomEvent("browser-notification-preference-updated", {
          detail: {
            enabled: browserNotificationsEnabled === true,
          },
        }),
      );

      triggerPageRefresh("Notification settings updated", theme, "success");
      closeOverlay();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Unable to save notification settings",
        theme,
        "error",
      );
    }
  };

  return (
    <Box
      p={isMobile ? "1rem" : "2rem"}
      bgcolor={theme.palette.background.alt}
      sx={{
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={closeOverlay}>
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              Edit Notification Settings
            </Typography>
          </Box>

          <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </Box>

        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            borderRadius: "12px",
            border: `1px solid ${theme.palette.primary.border2}`,
            boxShadow: theme.shadows[3],
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <FormControlLabel
            control={(
              <Switch
                checked={browserNotificationsEnabled}
                onChange={(event) => setBrowserNotificationsEnabled(event.target.checked)}
              />
            )}
            label="Browser notifications"
          />

          <FormControlLabel
            control={(
              <Switch
                checked={smsNotificationsEnabled}
                onChange={(event) => setSmsNotificationsEnabled(event.target.checked)}
              />
            )}
            label="SMS notifications"
          />

          <TextField
            select
            label="Quiet hours start"
            value={quietHoursStart}
            onChange={(event) => setQuietHoursStart(event.target.value)}
            fullWidth
          >
            {timeOptions.map((option) => (
              <MenuItem key={`quiet-start-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Quiet hours end"
            value={quietHoursEnd}
            onChange={(event) => setQuietHoursEnd(event.target.value)}
            fullWidth
          >
            {timeOptions.map((option) => (
              <MenuItem key={`quiet-end-${option.value}`} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Timezone"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            fullWidth
          >
            {TIMEZONE_OPTIONS.map((timezoneOption) => (
              <MenuItem key={timezoneOption} value={timezoneOption}>
                {timezoneOption}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistNotificationSettingsEditOverlay;
